import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/shell/PageHeader";
import { useAppState } from "@/state/AppStateProvider";
import { IntegrationCard } from "@/components/content/IntegrationCard";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, ShieldCheck, KeyRound, Plug, Send, CheckCircle2, AlertCircle, Database } from "lucide-react";
import { motion } from "framer-motion";
import { connectionsApi, integrationPlatform } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "Social Posting", label: "Social accounts" },
  { id: "Publishing", label: "Publishing" },
  { id: "Sources", label: "Sources" },
];

// Social platforms planned but not connectable yet (no managed Composio OAuth).
// They render as "Needs setup" with this note. See docs/planned-integrations.md.
const PLANNED_SOCIAL = {
  threads: "Planned — will post via the Meta Threads API using your Meta dev app.",
};

export default function Integrations() {
  const { state } = useAppState();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  // Live Composio publishing config + connected accounts.
  const [pubConfig, setPubConfig] = useState({ configured: false, supported_platforms: [] });
  const [connections, setConnections] = useState([]);

  const loadConnections = useCallback(async (signal) => {
    try {
      const [cfg, list] = await Promise.all([
        connectionsApi.config(signal),
        connectionsApi.list(signal),
      ]);
      if (cfg) setPubConfig(cfg);
      if (Array.isArray(list)) setConnections(list);
    } catch (_) { /* backend down — page still renders with mock toggles */ }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadConnections(controller.signal);
    return () => controller.abort();
  }, [loadConnections]);

  const connectionByPlatform = useMemo(() => {
    const map = {};
    connections.forEach((c) => { map[c.platform] = c; });
    return map;
  }, [connections]);

  // Web Research is a built-in capability, not a mock toggle. Ollama Web Search
  // uses the BYOK Ollama Cloud key; academic (OpenAlex) and browser need none.
  const ollamaReady = useMemo(
    () => (state.modelSettings?.providers || []).some((p) => p.id === "ollama_cloud" && p.configured),
    [state.modelSettings],
  );

  const list = useMemo(() => {
    let l = state.integrations;
    if (tab !== "all") l = l.filter((x) => x.category === tab);
    if (q.trim()) {
      const t = q.toLowerCase();
      l = l.filter((x) => `${x.name} ${x.description}`.toLowerCase().includes(t));
    }
    return l;
  }, [state.integrations, tab, q]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="integrations-page">
      <PageHeader
        eyebrow="Integrations"
        title="Connect your accounts. Keep the approval gate."
        description="Link the social accounts and publishing endpoints the agent can post to, and wire in the sources that feed your Content Inbox. Connections are secure and nothing publishes without your review."
        testId="integrations-page-header"
        actions={
          <>
            <Link
              to="/settings"
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border bg-[color:var(--surface-2)] px-4 text-sm font-semibold text-[color:var(--text-2)] shadow-sm transition-colors hover:bg-[color:var(--surface-3)] hover:text-foreground"
              style={{ borderColor: "var(--border-subtle)" }}
              data-testid="integrations-model-keys-link"
            >
              <KeyRound className="h-4 w-4" /> Model keys in Settings
            </Link>
            <button
              type="button"
              onClick={() => setTab("Social Posting")}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[color:var(--accent-blue)] px-4 text-sm font-semibold text-[color:var(--accent-blue-ink)] shadow-[0_10px_28px_rgba(18, 147, 79,0.25)] transition-colors hover:bg-[color:var(--accent-blue)]"
              data-testid="integrations-social-cta"
            >
              <Send className="h-4 w-4" /> Connect a social account
            </button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-[color:var(--surface-2)] px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <ShieldCheck className="h-4 w-4 text-[color:var(--accent-green)]" />
        <p className="text-xs text-foreground/85">Auto-publish is off by default. A post only goes out on its own if you arm that specific scheduled item on the Calendar — otherwise everything waits for your approval.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        <p className="text-xs text-foreground/85">
          Looking for OpenAI, Anthropic, Gemini, or other model keys? Those live in{" "}
          <Link to="/settings" className="font-semibold text-[color:var(--accent-cyan)] hover:underline">Settings → Model provider keys</Link>.
        </p>
      </div>

      {!pubConfig.configured && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border px-4 py-3" style={{ borderColor: "rgba(217,119,6,0.28)", backgroundColor: "rgba(217,119,6,0.08)" }} data-testid="integrations-composio-banner">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <p className="text-xs text-foreground/85">
            Live account connection isn't enabled yet. Set <code className="font-mono">COMPOSIO_API_KEY</code> in <code className="font-mono">backend/.env</code> and restart the backend to connect real accounts. Until then, toggles are preview-only.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent p-0 gap-1" data-testid="integrations-tabs">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.id} value={c.id} className="" data-testid={`integrations-tab-${c.id}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search integrations…" className="h-9 pl-8" data-testid="integrations-search" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
        </div>
      </div>

      {(() => {
        // Decorate each integration with its state, then group: connected on
        // top, available next, needs-setup at the bottom.
        const decorated = list.map((it) => {
          const platform = integrationPlatform(it.id);
          const isLive = pubConfig.supported_platforms?.includes(platform);
          const needsCustomAuth = pubConfig.needs_custom_auth?.includes(platform);
          const plannedNote = PLANNED_SOCIAL[platform];
          // Social platforms that need extra setup before connecting: X (own
          // paid API app) and Threads (planned via the Meta Threads API).
          let setup = null;
          if (needsCustomAuth) setup = { note: "Needs your own X developer app — the X API is paid. Planned." };
          else if (it.category === "Social Posting" && !isLive && plannedNote) setup = { note: plannedNote };
          const isResearch = it.id === "int_web_research";
          const connected = isResearch
            ? true // built-in: academic + browser always work; Ollama adds web search
            : isLive
              ? connectionByPlatform[platform]?.status === "active"
              : Boolean(it.connected);
          return { it, platform, isLive, setup, connected, isResearch, blocked: Boolean(setup) };
        });

        const renderCard = (d) =>
          d.isResearch ? (
            <ResearchCard key={d.it.id} ollamaReady={ollamaReady} />
          ) : (
            <IntegrationCard
              key={d.it.id}
              integration={d.it}
              live={d.isLive ? { configured: pubConfig.configured, platform: d.platform } : null}
              connection={d.isLive ? connectionByPlatform[d.platform] : null}
              setup={d.setup}
              onConnectionChange={() => loadConnections()}
            />
          );

        const connected = decorated.filter((d) => d.connected);
        const available = decorated.filter((d) => !d.connected && !d.blocked);
        const blocked = decorated.filter((d) => !d.connected && d.blocked);

        return (
          <div className="space-y-8" data-testid="integrations-groups">
            {connected.length > 0 && (
              <IntegrationGroup variant="connected" title="Connected" subtitle="Linked and active." count={connected.length}>
                {connected.map(renderCard)}
              </IntegrationGroup>
            )}
            {available.length > 0 && (
              <IntegrationGroup variant="available" title="Available to connect" subtitle="One click to link these accounts." count={available.length}>
                {available.map(renderCard)}
              </IntegrationGroup>
            )}
            {blocked.length > 0 && (
              <IntegrationGroup variant="blocked" title="Needs setup" subtitle="Requires your own developer-app credentials." count={blocked.length}>
                {blocked.map(renderCard)}
              </IntegrationGroup>
            )}
            {decorated.length === 0 && (
              <p className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
                No integrations match your filters.
              </p>
            )}
          </div>
        );
      })()}
    </motion.div>
  );
}

// Honest, state-driven card for the built-in Web Research capability.
function ResearchCard({ ollamaReady }) {
  const providers = [
    { name: "Free web search (Mojeek)", ok: true, note: "Default · no key required" },
    { name: "Academic (OpenAlex)", ok: true, note: "No key required" },
    { name: "Ollama Web Search", ok: ollamaReady, note: ollamaReady ? "Using your Ollama Cloud key" : "Optional — add a key in Settings" },
    { name: "Browser (Playwright)", ok: true, note: "Opens real pages, server-side" },
  ];
  return (
    <article
      data-testid="integration-card-int_web_research"
      className="flex flex-col gap-3 rounded-xl border bg-card p-4 inner-highlight"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
            <Database className="h-4 w-4 text-[color:var(--accent-cyan)]" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold tracking-[-0.005em] text-1">Web Research</h3>
            <p className="truncate text-[11px] text-muted-foreground">Sources · Built-in</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]" style={{ backgroundColor: "rgba(22,163,74,0.10)", borderColor: "rgba(22,163,74,0.24)", color: "#15803D" }}>
          <CheckCircle2 className="h-3 w-3" /> Active
        </span>
      </div>
      <p className="text-xs text-muted-foreground leading-[1.55]">Live web &amp; academic research that grounds agent runs — works out of the box, no key required. Chosen per run in Create and Blog.</p>
      <ul className="space-y-1.5 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
        {providers.map((p) => (
          <li key={p.name} className="flex items-center gap-2 text-[12px]">
            {p.ok
              ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
              : <KeyRound className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
            <span className="text-1">{p.name}</span>
            <span className="ml-auto truncate text-[11px] text-muted-foreground">{p.note}</span>
          </li>
        ))}
      </ul>
      <Link to="/settings" className="text-[11px] font-semibold text-[color:var(--accent-cyan)] hover:underline" data-testid="research-add-ollama-key">
        {ollamaReady ? "Manage the Ollama Cloud key in Settings →" : "Optional: add an Ollama key in Settings for richer snippets →"}
      </Link>
      <p className="rounded-md border border-dashed px-2.5 py-1.5 text-[11px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }} data-testid="research-v2-note">
        <span className="font-medium text-1">Coming in v2:</span> bring your own search provider (Firecrawl, Tavily, etc.) with a key, just like model providers.
      </p>
    </article>
  );
}

function IntegrationGroup({ variant, title, subtitle, count, children }) {
  const styles = {
    connected: {
      icon: CheckCircle2,
      iconClass: "text-emerald-500",
      countClass: "bg-emerald-500/12 text-emerald-600 border-emerald-500/25",
      panel: "rounded-2xl border border-emerald-500/20 bg-[linear-gradient(180deg,rgba(16,185,129,0.06),rgba(16,185,129,0.015))] p-3 ring-1 ring-emerald-400/15",
    },
    available: {
      icon: Plug,
      iconClass: "text-[color:var(--accent-cyan)]",
      countClass: "bg-white/[0.045] text-muted-foreground border-[color:var(--border-subtle)]",
      panel: "",
    },
    blocked: {
      icon: AlertCircle,
      iconClass: "text-amber-600",
      countClass: "bg-amber-500/10 text-amber-600 border-amber-500/20",
      panel: "opacity-90",
    },
  };
  const s = styles[variant] || styles.available;
  const Icon = s.icon;
  return (
    <section data-testid={`integrations-group-${variant}`}>
      <div className="mb-3 flex items-center gap-2.5">
        <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-lg", variant === "connected" ? "bg-emerald-500/12" : variant === "blocked" ? "bg-amber-500/10" : "bg-white/[0.045]")}>
          <Icon className={cn("h-4 w-4", s.iconClass)} />
        </span>
        <h2 className="text-sm font-semibold tracking-[-0.01em] text-1">{title}</h2>
        <span className={cn("inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border px-1.5 text-[11px] font-semibold", s.countClass)}>{count}</span>
        <span className="ml-1 hidden text-[12px] text-muted-foreground sm:inline">{subtitle}</span>
      </div>
      <div className={s.panel}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {children}
        </div>
      </div>
    </section>
  );
}

